import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Printer, Download, Search } from "lucide-react";

const buildSegments = (s) => {
  const d1 = s?.stage_1_days ?? 7;
  const d2 = s?.stage_2_days ?? 15;
  const p1 = s?.stage_1_discount ?? 30;
  const p2 = s?.stage_2_discount ?? 40;
  return [
    {
      key: "declined",
      label: `Declined — Awaiting Day ${d1}`,
      subtitle: `Days 0 – ${d1 - 1} since decline`,
      badgeClass: "bg-stone-500 text-white",
    },
    {
      key: "sent_30",
      label: `${p1}% Offer Sent (Day ${d1})`,
      subtitle: `Received the Day ${d1} · ${p1}% text`,
      badgeClass: "bg-blue-600 text-white",
    },
    {
      key: "sent_40",
      label: `${p2}% Offer Sent (Day ${d2})`,
      subtitle: `Received the Day ${d2} · ${p2}% text`,
      badgeClass: "bg-orange-500 text-white",
    },
    {
      key: "scheduled",
      label: "Scheduled Appointments",
      subtitle: "Booked a comeback",
      badgeClass: "bg-emerald-600 text-white",
    },
    {
      key: "called_live",
      label: "Called Live Person",
      subtitle: "Talked to an advisor",
      badgeClass: "bg-cyan-600 text-white",
    },
    {
      key: "no_response",
      label: "No Response",
      subtitle: `${d2}+ days · never converted`,
      badgeClass: "bg-red-500 text-white",
    },
  ];
};

// Filter the "declined — awaiting" bucket so only truly RECENT declines
// (within stage_1_days) count. Anything older is a system leak and shouldn't
// still be sitting in "waiting for day 7 text".
const daysSince = (isoStr) => {
  if (!isoStr) return Infinity;
  const d = new Date(isoStr);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

export default function SegmentsPage() {
  const [declines, setDeclines] = useState([]);
  const [advisors, setAdvisors] = useState({});
  const [settings, setSettings] = useState(null);
  const [sent30Ids, setSent30Ids] = useState(new Set());
  const [sent40Ids, setSent40Ids] = useState(new Set());
  const [only, setOnly] = useState("all"); // 'all' or a segment key
  const [query, setQuery] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/declines"),
      api.get("/advisors"),
      api.get("/settings"),
      api.get("/sms"),
    ])
      .then(([d, a, s, m]) => {
        setDeclines(d.data);
        const advMap = {};
        a.data.forEach((x) => (advMap[x.id] = { name: x.name, color: x.color || "#0f2b6b" }));
        setAdvisors(advMap);
        setSettings(s.data);
        // Build set of decline_ids that received a stage_1 (sent_30) SMS
        // and a stage_2 (sent_40) SMS — this is CUMULATIVE, so numbers match
        // the Overview KPIs (15% Sent 183, 15% Sent 167) instead of just
        // showing declines currently paused between stages.
        const s30 = new Set();
        const s40 = new Set();
        m.data.forEach((msg) => {
          if (msg.stage === "stage_30") s30.add(msg.decline_id);
          if (msg.stage === "stage_40") s40.add(msg.decline_id);
        });
        setSent30Ids(s30);
        setSent40Ids(s40);
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, []);

  const SEGMENTS = useMemo(() => buildSegments(settings), [settings]);

  const filtered = useMemo(() => {
    if (!query.trim()) return declines;
    const q = query.trim().toLowerCase();
    return declines.filter((d) => {
      const advisorName = (advisors[d.advisor_id]?.name || "").toLowerCase();
      const services = (d.service_items || []).map((s) => s.name).join(" ").toLowerCase();
      return (
        (d.customer_name || "").toLowerCase().includes(q) ||
        (d.customer_phone || "").toLowerCase().includes(q) ||
        (d.vehicle || "").toLowerCase().includes(q) ||
        advisorName.includes(q) ||
        services.includes(q)
      );
    });
  }, [declines, query, advisors]);

  const grouped = useMemo(() => {
    const g = {};
    SEGMENTS.forEach((s) => (g[s.key] = []));
    const d1 = settings?.stage_1_days ?? 7;
    filtered.forEach((d) => {
      // Cumulative buckets that match the Overview KPIs:
      // a decline is counted in sent_30 if it ever received a stage_1 SMS,
      // in sent_40 if it ever received a stage_2 SMS, PLUS in its current
      // status bucket (scheduled / called_live / no_response / declined).
      if (sent30Ids.has(d.id) && g.sent_30) g.sent_30.push(d);
      if (sent40Ids.has(d.id) && g.sent_40) g.sent_40.push(d);
      if (d.status === "declined" && g.declined) {
        // Only count as "awaiting Day X" if the decline is actually recent
        if (daysSince(d.decline_date) < d1) {
          g.declined.push(d);
        }
      } else if (
        g[d.status] &&
        d.status !== "sent_30" &&
        d.status !== "sent_40" &&
        d.status !== "declined"
      ) {
        g[d.status].push(d);
      }
    });
    return g;
  }, [filtered, SEGMENTS, sent30Ids, sent40Ids, settings]);

  const exportCsv = (segKey, segLabel, rows) => {
    const header = ["Customer", "Phone", "Vehicle", "Declined Services", "Advisor", "Decline Date", "Days Ago"];
    const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.join(",")];
    rows.forEach((d) => {
      lines.push([
        q(d.customer_name),
        q(d.customer_phone),
        q(d.vehicle),
        q((d.service_items || []).map((s) => s.name).join(" | ")),
        q(advisors[d.advisor_id]?.name || ""),
        q(new Date(d.decline_date).toLocaleDateString()),
        q(daysSince(d.decline_date)),
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `decline-drive-${segKey}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const visibleSegments = only === "all" ? SEGMENTS : SEGMENTS.filter((s) => s.key === only);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Customer Segments" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Reports</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Customer Segments</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Every customer grouped by where they are in the recovery pipeline. Tap a tile below to filter to
            one segment — the full list appears underneath. Tap the same tile again (or &quot;Show All&quot;) to see
            everything.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, vehicle…"
              className="pl-9 w-64"
              data-testid="segment-search"
            />
          </div>
          {only !== "all" && (
            <Button variant="outline" size="sm" onClick={() => setOnly("all")} data-testid="clear-segment-filter">
              Show All
            </Button>
          )}
          <Button variant="outline" onClick={() => window.print()} data-testid="print-segments-btn">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print
          </Button>
        </div>
      </div>

      {/* Segment count strip — click any tile to filter to that segment */}
      <div className="mb-2 no-print flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono-label text-muted-foreground">
          {only === "all" ? "Tap a tile to filter" : `Filtered to: ${SEGMENTS.find((s) => s.key === only)?.label}`}
        </div>
        <div className="text-xs text-muted-foreground">
          {filtered.length} customer{filtered.length === 1 ? "" : "s"} total
        </div>
      </div>
      {/* MPI category legend */}
      <div className="mb-3 no-print flex items-center gap-4 text-xs text-muted-foreground flex-wrap" data-testid="mpi-legend">
        <span className="font-mono-label">MPI item type:</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500" /> Safety-critical
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-500" /> Repair
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" /> Maintenance
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6 no-print">
        {SEGMENTS.map((s) => {
          const count = grouped[s.key]?.length || 0;
          const active = only === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setOnly(active ? "all" : s.key)}
              data-testid={`seg-btn-${s.key}`}
              className={`p-3 rounded-md border text-left transition-colors ${
                active
                  ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                  : count === 0
                  ? "border-border opacity-60 hover:opacity-100 hover:bg-muted"
                  : "border-border hover:bg-muted"
              }`}
            >
              <div className="font-mono-label text-muted-foreground text-[0.55rem] leading-tight">
                {s.label.split(" — ")[0].split(" (")[0]}
              </div>
              <div className="font-display text-2xl font-bold tracking-tight mt-1">
                {count}
              </div>
              {s.subtitle && (
                <div className="text-[0.6rem] text-muted-foreground mt-1 leading-tight">
                  {s.subtitle}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {visibleSegments.map((seg, idx) => {
        const rows = grouped[seg.key] || [];
        return (
          <Card
            key={seg.key}
            className={`p-0 overflow-hidden border-border mb-6 ${idx > 0 ? "page-break-before" : ""}`}
            data-testid={`segment-${seg.key}`}
          >
            <div className="p-5 border-b border-border flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Badge className={seg.badgeClass}>{seg.label}</Badge>
                </div>
                <div className="font-mono-label text-muted-foreground mt-2">
                  {rows.length} customer{rows.length === 1 ? "" : "s"} in this segment
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => exportCsv(seg.key, seg.label, rows)}
                  disabled={rows.length === 0}
                  className="no-print"
                  data-testid={`export-${seg.key}`}
                >
                  <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                  CSV
                </Button>
                <div className="text-right text-xs text-muted-foreground print-only">
                  Decline Drive™ by Ayers Enterprises
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Declined Services</TableHead>
                  <TableHead>Advisor</TableHead>
                  <TableHead>Declined On</TableHead>
                  <TableHead className="text-right">Days Ago</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-6 text-sm text-muted-foreground">
                      No customers in this segment yet.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((d) => {
                  const hasSafety = (d.service_items || []).some((s) => s.category === "safety");
                  const catDot = (c) =>
                    c === "safety"
                      ? "bg-red-500"
                      : c === "maintenance"
                      ? "bg-emerald-500"
                      : "bg-amber-500";
                  return (
                    <TableRow
                      key={d.id}
                      className={hasSafety ? "bg-red-50/40 dark:bg-red-950/10" : ""}
                      data-testid={`seg-row-${d.id}`}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{d.customer_name}</span>
                          {hasSafety && (
                            <Badge className="bg-red-500 text-white text-[0.55rem] px-1.5 py-0" data-testid={`safety-badge-${d.id}`}>
                              SAFETY
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm font-mono">{d.customer_phone}</TableCell>
                      <TableCell className="text-sm">{d.vehicle}</TableCell>
                      <TableCell className="text-sm max-w-[280px]">
                        <div className="flex flex-wrap gap-x-1.5 gap-y-1">
                          {d.service_items.map((s, i) => (
                            <span key={i} className="inline-flex items-center gap-1">
                              <span className={`w-1.5 h-1.5 rounded-full ${catDot(s.category)}`} />
                              <span>
                                {s.name}
                                {i < d.service_items.length - 1 ? "," : ""}
                              </span>
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {advisors[d.advisor_id] ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="w-2.5 h-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                              style={{ backgroundColor: advisors[d.advisor_id].color }}
                              data-testid={`adv-dot-${d.advisor_id}`}
                            />
                            <span>{advisors[d.advisor_id].name}</span>
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(d.decline_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {(() => {
                          const n = daysSince(d.decline_date);
                          return n === 0 ? "Today" : n === 1 ? "1 day" : `${n} days`;
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        );
      })}
      <ScrollToTopButton testid="segments-scroll-top" />
    </DashboardShell>
  );
}
